import React, { Component } from 'react';
import Helmet from 'react-helmet';
import { graphql } from 'gatsby';
import MDXRenderer from 'gatsby-mdx/mdx-renderer';
import styled, { injectGlobal } from 'react-emotion';
import { Layout, Link } from '$components';
import NextPrevious from '../components/NextPrevious';
import '../components/styles.css';
import config from '../../config';
import { initAuth, isLoggedIn, handleLogin } from '../service/auth';

const forcedNavOrder = config.sidebar.forcedNavOrder;
const HOME_PAGE_NAME = 'GettingStarted';

const documentGlobal = typeof document !== 'undefined' && document;

injectGlobal`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html, body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: 16px;
  }

  a {
    transition: color 0.15s;
    color: #c437af;
  }
`;

export default class MDXRuntimeTest extends Component {
  componentWillMount() {
    if (
      documentGlobal &&
      documentGlobal.location &&
      documentGlobal.location.pathname === '/'
    ) {
      document.location.href = `/${HOME_PAGE_NAME}`;
      return;
    }
    initAuth();
  }

  componentDidMount() {
    if (
      documentGlobal &&
      documentGlobal.location &&
      documentGlobal.location.pathname === '/'
    ) {
      return;
    }

    if (!this.canSeePage()) {
      this.doLogin();
    }
  }

  componentDidUpdate() {
    if (!this.canSeePage()) {
      this.doLogin();
    }
  }

  doLogin = () => {
    handleLogin(success => {
      if (success) {
        this.forceUpdate();
      } else if (!isLoggedIn()) {
        document.location.href = `/${HOME_PAGE_NAME}`;
      }
    });
  };

  canSeePage = () => {
    const { data } = this.props;
    const page = data.mdx.fields.slug;
    return page === `/${HOME_PAGE_NAME}` || isLoggedIn();
  };

  render() {
    if (
      documentGlobal &&
      documentGlobal.location &&
      documentGlobal.location.pathname === '/'
    ) {
      return null;
    }

    const { data } = this.props;
    const {
      allMdx,
      mdx,
      site: {
        siteMetadata: { docsLocation, title }
      }
    } = data;

    const navItems = allMdx.edges
      .map(({ node }) => node.fields.slug)
      .filter(slug => slug !== '/')
      .sort()
      .reduce(
        (acc, cur) => {
          if (forcedNavOrder.find(url => url === cur)) {
            return { ...acc, [cur]: [cur] };
          }
          throw new Error(`Cannot find ${cur} in content`);
        },
        { items: [] }
      );

    const nav = forcedNavOrder
      .reduce((acc, cur) => {
        if (!navItems[cur]) {
          throw new Error(`${cur} cannot be found in pages`);
        }
        return acc.concat(navItems[cur]);
      }, [])
      .concat(navItems.items)
      .map(slug => {
        try {
          const { node } = allMdx.edges.find(({ node }) => {
            return node.fields.slug === slug;
          });

          return { title: node.fields.title, url: node.fields.slug };
        } catch (e) {
          throw e;
        }
      });

    // meta tags
    const metaTitle = mdx.frontmatter.metaTitle;
    const metaDescription = mdx.frontmatter.metaDescription;
    let canonicalUrl = config.gatsby.siteUrl;
    canonicalUrl =
      config.gatsby.pathPrefix !== '/'
        ? canonicalUrl + config.gatsby.pathPrefix
        : canonicalUrl;
    canonicalUrl = canonicalUrl + mdx.fields.slug;
    const canSee = this.canSeePage();

    return (
      <Layout {...this.props}>
        <Helmet>
          {metaTitle ? <title>{metaTitle}</title> : null}
          {metaTitle ? <meta name="title" content={metaTitle} /> : null}
          {metaDescription ? (
            <meta name="description" content={metaDescription} />
          ) : null}
          {metaTitle ? <meta property="og:title" content={metaTitle} /> : null}
          {metaDescription ? (
            <meta property="og:description" content={metaDescription} />
          ) : null}
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
        <div className={'titleWrapper'}>
          <h1 className={'title'}>{mdx.fields.title}</h1>
        </div>
        <div className={'mainWrapper'}>
          {canSee && <MDXRenderer>{mdx.code.body}</MDXRenderer>}
        </div>
        <div className={'addPaddTopBottom'}>
          <NextPrevious mdx={mdx} nav={nav} />
        </div>
      </Layout>
    );
  }
}

export const pageQuery = graphql`
  query($id: String!) {
    site {
      siteMetadata {
        title
        docsLocation
      }
    }
    mdx(fields: { id: { eq: $id } }) {
      fields {
        id
        title
        slug
      }
      code {
        body
      }
      tableOfContents
      parent {
        ... on File {
          relativePath
        }
      }
      frontmatter {
        metaTitle
        metaDescription
      }
    }
    allMdx {
      edges {
        node {
          fields {
            slug
            title
          }
        }
      }
    }
  }
`;
